<template>
  <div class="subscription-badge" :class="badgeClass" @click="handleClick">
    <template v-if="loading">
      <span class="badge-loading">
        <i class="bi bi-hourglass-split"></i>
      </span>
    </template>
    <template v-else-if="hasActiveSubscription">
      <i class="bi bi-star-fill badge-icon"></i>
      <span class="badge-text">
        {{ planName }}
        <span class="badge-usage" v-if="showUsage">{{ usageText }}</span>
      </span>
    </template>
    <template v-else>
      <i class="bi bi-lightning badge-icon"></i>
      <span class="badge-text">{{ freeText }}</span>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

export default {
  name: 'SubscriptionBadge',

  props: {
    // Show usage stats (e.g., "245/900 min")
    showUsage: {
      type: Boolean,
      default: true
    },
    // Chatbot name for fetching subscription
    chatbotName: {
      type: String,
      default: 'meetscribe'
    },
    // Make badge clickable
    clickable: {
      type: Boolean,
      default: true
    },
    // Compact mode (icon only on mobile)
    compact: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapState('user', ['isAuthenticated']),
    ...mapGetters('subscription', [
      'hasActiveSubscription',
      'currentPlanName',
      'currentPlanId',
      'remainingMinutes',
      'totalMinutes',
      'isLoading',
      'isFreePlan'
    ]),

    loading() {
      return this.isLoading;
    },

    planName() {
      return this.currentPlanName;
    },

    usageText() {
      if (!this.totalMinutes) return '';
      return `${this.remainingMinutes}/${this.totalMinutes} min`;
    },

    freeText() {
      return 'Pay-as-you-go';
    },

    badgeClass() {
      return {
        'badge-pro': this.currentPlanId === 'pro',
        'badge-business': this.currentPlanId === 'business',
        'badge-lite': this.currentPlanId === 'lite',
        'badge-free': this.isFreePlan,
        'badge-clickable': this.clickable,
        'badge-compact': this.compact
      };
    }
  },

  watch: {
    // Watch for authentication changes and fetch subscription when user logs in
    isAuthenticated: {
      handler(newVal) {
        if (newVal) {
          this.fetchSubscriptionStatus(this.chatbotName);
        }
      },
      immediate: false
    }
  },

  mounted() {
    // Only fetch if already authenticated
    if (this.isAuthenticated) {
      this.fetchSubscriptionStatus(this.chatbotName);
    }
  },

  methods: {
    ...mapActions('subscription', ['fetchSubscriptionStatus']),

    handleClick() {
      if (this.clickable) {
        this.$router.push({ name: 'Subscription' });
      }
    }
  }
};
</script>

<style scoped>
.subscription-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background-color: var(--yt-bg-tertiary, #f3f4f6);
  color: var(--yt-text-secondary, #6b7280);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.subscription-badge.badge-clickable {
  cursor: pointer;
}

.subscription-badge.badge-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Plan-specific colors */
.subscription-badge.badge-pro {
  background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
  color: white;
}

.subscription-badge.badge-business {
  background: linear-gradient(135deg, #059669 0%, #10B981 100%);
  color: white;
}

.subscription-badge.badge-lite {
  background: linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%);
  color: white;
}

.subscription-badge.badge-free {
  background-color: var(--yt-bg-tertiary, #f3f4f6);
  color: var(--yt-text-secondary, #6b7280);
  border: 1px solid var(--yt-border-color, #e5e7eb);
}

/* Badge icon */
.badge-icon {
  font-size: 12px;
}

.badge-pro .badge-icon,
.badge-business .badge-icon {
  color: #fbbf24;
}

.badge-free .badge-icon {
  color: var(--yt-text-muted, #9ca3af);
}

/* Badge text */
.badge-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Usage text */
.badge-usage {
  font-weight: 500;
  opacity: 0.9;
  font-size: 12px;
}

.badge-free .badge-usage {
  color: var(--yt-text-muted, #9ca3af);
}

/* Loading state */
.badge-loading {
  display: flex;
  align-items: center;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Compact mode */
.subscription-badge.badge-compact {
  padding: 6px 8px;
}

.subscription-badge.badge-compact .badge-text {
  display: none;
}

@media (min-width: 768px) {
  .subscription-badge.badge-compact .badge-text {
    display: flex;
  }

  .subscription-badge.badge-compact {
    padding: 6px 12px;
  }
}

/* Dark mode */
html[data-bs-theme="dark"] .subscription-badge.badge-free {
  background-color: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4B5563);
  color: var(--yt-text-secondary, #D1D5DB);
}

html[data-bs-theme="dark"] .subscription-badge.badge-clickable:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>
